// src/hooks/useConfiguracionEmpresa.ts
import { useCallback, useEffect, useState } from 'react';
import api from '../utils/axios';

export interface ConfiguracionEmpresa {
  nombre: string;
  nit: string;
  direccion: string;
  telefono: string;
  email: string;
  sitioWeb?: string;
  logo?: string;
}

const VACIA: ConfiguracionEmpresa = {
  nombre: '', nit: '', direccion: '', telefono: '', email: '', sitioWeb: '', logo: '',
};

/** Caché en memoria: los datos del emisor no cambian entre impresiones. */
let cache: ConfiguracionEmpresa | null = null;

/**
 * Datos del emisor para la representación gráfica de la factura.
 * Vienen de la tabla `empresas`; antes estaban escritos a mano en el PDF, lo
 * que hacía que toda factura saliera con un NIT que no era el real.
 */
export const useConfiguracionEmpresa = () => {
  const [empresa, setEmpresa] = useState<ConfiguracionEmpresa>(cache ?? VACIA);
  const [cargando, setCargando] = useState(!cache);

  const cargar = useCallback(async () => {
    if (cache) { setEmpresa(cache); setCargando(false); return; }
    try {
      const { data } = await api.post('/administracion/empresas/findAll', {});
      const lista = Array.isArray(data) ? data : data?.items ?? [];
      // Se toma la primera empresa activa; el sistema es de una sola empresa
      // por instalación (ver multi-tenant pendiente).
      const e = lista.find((x: any) => x.Activo !== false) ?? lista[0];
      if (e) {
        // La tabla `empresas` no tiene telefono ni correo; se dejan vacios y
        // la factura simplemente omite esas lineas.
        cache = {
          nombre: e.Nombre ?? '',
          nit: e.NIT ?? '',
          direccion: e.Direccion ?? '',
          telefono: e.Telefono ?? '',
          email: e.Correo ?? e.Email ?? '',
          sitioWeb: e.SitioWeb ?? '',
          logo: e.Logo ?? '',
        };
        setEmpresa(cache);
      }
    } catch {
      // Sin datos de empresa la factura se imprime igual, solo sin encabezado.
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  return { empresa, cargando, recargar: () => { cache = null; return cargar(); } };
};

export default useConfiguracionEmpresa;
